import { defineStore } from 'pinia'
import { auth } from '../FirebaseConfig'
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  type User as FirebaseUser,
  type UserCredential
} from 'firebase/auth'
import { refreshIdToken, removeToken } from '../lib/authUtil'
import { Role } from '../lib/role'
import { OAuthProvider, getAdditionalUserInfo } from 'firebase/auth'
import type { AuthError } from 'firebase/auth'

const LOGIN_API_ENDPOINT = import.meta.env.VITE_LOGIN_API_ENDPOINT
const PROVIDER_LINK_API_ENDPOINT = import.meta.env.VITE_PROVIDER_LINK_API_ENDPOINT

export interface User {
  userId: string
  userName: string
  tenantId: string
  role: Role // 管理方法検討
}

interface AuthResponse {
  result: string
  reason: string
  user: {
    user_id: string
    user_name: string
    tenant_id: string
    'allowed-roles': string[]
    'default-role': string
  }
}

export class LoginError extends Error {
  constructor(
    public code: string,
    message?: string
  ) {
    super(message)
    this.name = 'CustomError'
  }
}

export const useUserStore = defineStore('userStore', {
  state: () => ({
    user: JSON.parse(localStorage.getItem('user') ?? 'null') as User | null
  }),
  getters: {
    isLoggedIn(): boolean {
      return this.user !== null
    },
    userInfo(): User | null {
      return this.user
    },
    role(): Role {
      if (!this.user) {
        return Role.Unknown
      }
      return this.user.role
    }
  },
  actions: {
    async login(firebaseTenantId: string, email: string, password: string): Promise<void> {
      auth.tenantId = firebaseTenantId
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      await this.processLogin(userCredential)
    },

    async loginWithCredential(userCredential: UserCredential): Promise<void> {
      await this.processLogin(userCredential)
    },

    async loginWithIdentityPlatform(
      firebaseTenantId: string,
      provider: OAuthProvider
    ): Promise<void> {
      auth.tenantId = firebaseTenantId
      const userCredential = await signInWithPopup(auth, provider).catch(async (error) => {
        const authError = error as AuthError
        const errorCode = authError.code
        const email = authError.customData.email
        const pendingCred = OAuthProvider.credentialFromError(error)
        // account-exists-with-different-credential エラーの場合は、Email/Passのアカウントにリンクをする
        if (
          errorCode === 'auth/account-exists-with-different-credential' &&
          pendingCred !== null &&
          email !== undefined
        ) {
          const requestBody = {
            idToken: pendingCred.idToken,
            tenantId: firebaseTenantId,
            providerId: pendingCred.providerId,
            accessToken: pendingCred.accessToken
          }
          const response = await fetch(PROVIDER_LINK_API_ENDPOINT, {
            mode: 'cors',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          })
          if (!response.ok) {
            throw new LoginError('provider-link-failed', 'ユーザの紐付けに失敗しました。')
          }
          // 紐付けが成功しているはずなので、再度ログインを試す
          return await signInWithCredential(auth, pendingCred)
        } else if (errorCode === 'auth/user-disabled') {
          throw new LoginError(
            'user-disabled',
            'このユーザーは無効化されています。管理者にお問い合わせください。'
          )
        } else {
          throw new LoginError('login-failed', 'システムエラー(SSO設定を見直してください。)')
        }
      })
      if (userCredential === undefined) {
        throw new LoginError('user-credential-not-found', '認証情報が見つかりませんでした。')
      }
      const isNewUser = getAdditionalUserInfo(userCredential)?.isNewUser
      // 初回接続ユーザは許可しない
      if (isNewUser === true) {
        auth.currentUser?.delete()
        throw new LoginError(
          'user-not-found',
          'Co-Assignユーザが見つかりませんでした。管理者にお問合せください。'
        )
      } else {
        await this.processLogin(userCredential)
      }
    },

    async processLogin(userCredential: UserCredential): Promise<void> {
      if (userCredential.user.tenantId == null) {
        throw new Error('FirebaseTenantId not found')
      }
      const authResponse = await this.fetchAuthData()
      if (
        !authResponse.user.user_id ||
        !authResponse.user.tenant_id ||
        !authResponse.user.user_name
      ) {
        throw new Error('Co-Assign user not found')
      }
      let role = Role.Unknown
      if (Object.values(Role).includes(authResponse.user['default-role'] as Role)) {
        role = authResponse.user['default-role'] as Role
      }

      refreshIdToken()

      const user: User = {
        userId: authResponse.user.user_id,
        userName: authResponse.user.user_name,
        tenantId: authResponse.user.tenant_id,
        role: role
      }
      this.setUser(user)
    },

    async updateAuthState(authUser: FirebaseUser | null) {
      if (authUser) {
        refreshIdToken()
      } else {
        this.logout()
      }
    },

    setUser(user: User) {
      this.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },

    logout() {
      this.user = null
      localStorage.removeItem('user')
      removeToken()
      auth.signOut()
    },

    async fetchAuthData(): Promise<AuthResponse> {
      if (auth.currentUser === null) {
        throw new Error('User not found')
      }
      const idToken = await auth.currentUser.getIdToken()
      const response = await fetch(LOGIN_API_ENDPOINT, {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        }
      })
      if (!response.ok) {
        throw new Error(`Authorize API Error ${response}`)
      }
      return response.json()
    }
  }
})
